import { createContext, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../config/api";

const DataCacheContext = createContext(null);

// Cache expiration time (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

export function DataCacheProvider({ children }) {
  const cacheRef = useRef({});
  const [loading, setLoading] = useState({});
  const pendingRequests = useRef({});
  const [preloadComplete, setPreloadComplete] = useState(false);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  const isCacheValid = useCallback((key) => {
    const cached = cacheRef.current[key];
    if (!cached) return false;
    return Date.now() - cached.timestamp < CACHE_TTL;
  }, []);

  const fetchWithCache = useCallback(async (key, url, forceRefresh = false) => {
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && isCacheValid(key)) {
      return cacheRef.current[key].data;
    }

    // If there's already a pending request for this key, wait for it
    if (pendingRequests.current[key]) {
      return pendingRequests.current[key];
    }

    // Set loading state
    setLoading(prev => ({ ...prev, [key]: true }));

    // Create the promise and store it
    const fetchPromise = (async () => {
      try {
        const response = await axios.get(`${API}${url}`, getAuthHeader());
        const data = response.data;
        
        cacheRef.current[key] = { data, timestamp: Date.now() };
        
        return data;
      } catch (error) {
        console.error(`Error fetching ${key}:`, error);
        throw error;
      } finally {
        setLoading(prev => ({ ...prev, [key]: false }));
        delete pendingRequests.current[key];
      }
    })();

    pendingRequests.current[key] = fetchPromise;
    return fetchPromise;
  }, [isCacheValid, getAuthHeader]);

  // Pre-defined fetchers for common data
  const fetchClients = useCallback((forceRefresh = false) => {
    return fetchWithCache("clients", "/clients", forceRefresh);
  }, [fetchWithCache]);

  const fetchTeamMembers = useCallback((forceRefresh = false) => {
    return fetchWithCache("teamMembers", "/users/team", forceRefresh);
  }, [fetchWithCache]);

  const fetchCases = useCallback((forceRefresh = false) => {
    return fetchWithCache("cases", "/cases", forceRefresh);
  }, [fetchWithCache]);

  const fetchCasesBC = useCallback((forceRefresh = false) => {
    return fetchWithCache("casesBC", "/cases-bc", forceRefresh);
  }, [fetchWithCache]);

  const fetchEvidenceBC = useCallback((forceRefresh = false) => {
    return fetchWithCache("evidenceBC", "/evidenta-bc", forceRefresh);
  }, [fetchWithCache]);

  const fetchTasks = useCallback((forceRefresh = false) => {
    return fetchWithCache("tasks", "/tasks", forceRefresh);
  }, [fetchWithCache]);

  const fetchAppointments = useCallback((forceRefresh = false) => {
    return fetchWithCache("appointments", "/appointments", forceRefresh);
  }, [fetchWithCache]);

  // Preload all data in background after login
  const preloadAllData = useCallback(async () => {
    console.log("🚀 Preloading all data in background...");
    setPreloadComplete(false);
    
    try {
      // Fetch all data in parallel (in background, don't wait)
      await Promise.all([
        fetchClients().catch(e => console.log("Preload clients failed:", e.message)),
        fetchCases().catch(e => console.log("Preload cases failed:", e.message)),
        fetchCasesBC().catch(e => console.log("Preload casesBC failed:", e.message)),
        fetchEvidenceBC().catch(e => console.log("Preload evidenceBC failed:", e.message)),
        fetchTasks().catch(e => console.log("Preload tasks failed:", e.message)),
        fetchTeamMembers().catch(e => console.log("Preload team failed:", e.message)),
        fetchAppointments().catch(e => console.log("Preload appointments failed:", e.message)),
      ]);
      
      console.log("✅ All data preloaded successfully");
      setPreloadComplete(true);
    } catch (error) {
      console.error("Preload error:", error);
    }
  }, [fetchClients, fetchCases, fetchCasesBC, fetchEvidenceBC, fetchTasks, fetchTeamMembers, fetchAppointments]);

  // Invalidate specific cache
  const invalidateCache = useCallback((key) => {
    delete cacheRef.current[key];
  }, []);

  // Invalidate all cache
  const invalidateAllCache = useCallback(() => {
    cacheRef.current = {};
    setPreloadComplete(false);
  }, []);

  // Get cached data without fetching
  const getCachedData = useCallback((key) => {
    if (isCacheValid(key)) {
      return cacheRef.current[key].data;
    }
    return null;
  }, [isCacheValid]);

  const value = {
    loading,
    preloadComplete,
    fetchWithCache,
    fetchClients,
    fetchTeamMembers,
    fetchCases,
    fetchCasesBC,
    fetchEvidenceBC,
    fetchTasks,
    fetchAppointments,
    preloadAllData,
    invalidateCache,
    invalidateAllCache,
    getCachedData,
    isCacheValid
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error("useDataCache must be used within a DataCacheProvider");
  }
  return context;
}
